import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';

interface YearlyData {
  year: string;
  records: number;
}

interface SpeciesStatsResponse {
  monthlyData: Array<Record<string, string | number>>;
  yearlyData: YearlyData[];
}

const yearlyChartConfig = {
  records: { label: 'Records', color: 'hsl(var(--primary))' },
} satisfies ChartConfig;

interface SpeciesYearlyChartProps {
  orgId: string;
  speciesId: string;
}

export function SpeciesYearlyChart({ orgId, speciesId }: SpeciesYearlyChartProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['speciesStats', orgId, speciesId],
    queryFn: async () => {
      const res = await fetch(`/api/org/${orgId}/species/${speciesId}/stats`);
      if (!res.ok) throw new Error('Failed to fetch species stats');
      return res.json() as Promise<SpeciesStatsResponse>;
    },
    enabled: !!orgId && !!speciesId,
  });

  const yearlyData = data?.yearlyData ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Records per Year</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : yearlyData.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No records for this species.</div>
        ) : (
          <ChartContainer config={yearlyChartConfig} className="h-[300px] w-full">
            <ResponsiveContainer>
              <BarChart data={yearlyData} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis dataKey="year" tickLine={false} axisLine={false} tickMargin={8} />
                <YAxis tickLine={false} axisLine={false} tickMargin={8} allowDecimals={false} />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      formatter={(value) => [`${value} records`, 'Count']}
                    />
                  }
                />
                <Bar dataKey="records" radius={[4, 4, 0, 0]}>
                  {yearlyData.map((year) => (
                    <Cell key={year.year} fill="hsl(var(--primary))" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
